import { useState, useEffect, useRef } from 'react';

// GPS status constants
export const GPS_STATUS = {
  CHECKING: 'checking',
  PROMPT_PENDING: 'prompt_pending',
  WAITING: 'waiting',
  SUCCESS: 'success',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
  TIMEOUT: 'timeout',
  UNSUPPORTED: 'unsupported'
};

// Constants
const GPS_TIMEOUT_MS = 15000; // 15 seconds
const GPS_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 14000, // Slightly less than our timeout
  maximumAge: 0
};

// Helper to check if coordinates are valid
export const isValidCoordinate = (coord) => {
  return coord != null &&
         typeof coord === 'object' &&
         isFinite(coord.lat) &&
         isFinite(coord.lng) &&
         Math.abs(coord.lat) <= 90 &&
         Math.abs(coord.lng) <= 180;
};

export const isValidLatLng = (lat, lng) => {
  return lat != null &&
         lng != null &&
         isFinite(lat) &&
         isFinite(lng) &&
         Math.abs(lat) <= 90 &&
         Math.abs(lng) <= 180;
};

// Main GPS tracking hook
export function useGPSTracking(shouldRequest = true, onPositionUpdate = null) {
  const [gpsStatus, setGpsStatus] = useState(GPS_STATUS.CHECKING);
  const [position, setPosition] = useState(null);
  const [gpsAvailable, setGpsAvailable] = useState(null);
  const [orientation, setOrientation] = useState(0);
  
  const timeoutRef = useRef(null);
  const watchIdRef = useRef(null);
  const permissionStatusRef = useRef(null);

  // Handle device orientation
  useEffect(() => {
    if (!window.DeviceOrientationEvent) {
      console.log('[GPS] DeviceOrientation is not supported by this browser');
      return;
    }

    const handleOrientation = (event) => {
      const alpha = event.alpha;
      // Commenting out setOrientation to prevent unnecessary re-renders
      // This was causing GoogleOneTap to unmount/remount on device movement
      // Uncomment only if orientation value is actually needed somewhere
      // setOrientation(alpha || 0);
      
      // Still log for debugging if needed
      // console.log('[GPS] Device orientation update:', {
      //   alpha: event.alpha,
      //   beta: event.beta,
      //   gamma: event.gamma
      // });
    };

    window.addEventListener('deviceorientation', handleOrientation);
    
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, []);

  // Main GPS tracking effect
  useEffect(() => {
    if (!shouldRequest) {
      console.log('[GPS] Tracking disabled by shouldRequest parameter');
      return;
    }

    if (!navigator.geolocation) {
      setGpsStatus(GPS_STATUS.UNSUPPORTED);
      console.log('[GPS] Status: UNSUPPORTED - Geolocation not available');
      setGpsAvailable(false);
      return;
    }

    console.log('[GPS] Status: CHECKING - Starting GPS request');
    setGpsStatus(GPS_STATUS.PROMPT_PENDING);
    console.log('[GPS] Status: PROMPT_PENDING - Waiting for user permission');
    
    // Set timeout for the entire GPS request
    timeoutRef.current = setTimeout(() => {
      const currentStatus = gpsStatus;
      if (currentStatus === GPS_STATUS.PROMPT_PENDING || currentStatus === GPS_STATUS.WAITING) {
        setGpsStatus(GPS_STATUS.TIMEOUT);
        console.log('[GPS] Status: TIMEOUT - Request took too long');
        setGpsAvailable(false);
      }
    }, GPS_TIMEOUT_MS);

    // Check permissions API if available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => {
          permissionStatusRef.current = result;
          
          console.log('[GPS] Initial permission state:', result.state);
          
          if (result.state === 'granted') {
            setGpsStatus(GPS_STATUS.WAITING);
            console.log('[GPS] Status: WAITING - Permission already granted, getting position...');
          } else if (result.state === 'denied') {
            setGpsStatus(GPS_STATUS.DENIED);
            console.log('[GPS] Status: DENIED - Permission already denied');
            setGpsAvailable(false);
            clearTimeout(timeoutRef.current);
            return;
          }
          
          // Listen for permission changes
          result.onchange = () => {
            console.log('[GPS] Permission state changed to:', result.state);
            if (result.state === 'denied') {
              setGpsStatus(GPS_STATUS.DENIED);
              setGpsAvailable(false);
            } else if (result.state === 'granted' && gpsStatus === GPS_STATUS.PROMPT_PENDING) {
              setGpsStatus(GPS_STATUS.WAITING);
              console.log('[GPS] Status: WAITING - Permission granted, getting position...');
            }
          };
        })
        .catch((error) => {
          console.log('[GPS] Permissions API not available or error:', error);
        });
    }

    // Request current position
    const successHandler = (position) => {
      clearTimeout(timeoutRef.current);
      setGpsStatus(GPS_STATUS.SUCCESS);
      console.log('[GPS] Status: SUCCESS - Position received:', {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      });
      
      const pos = { 
        lat: position.coords.latitude, 
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
        timestamp: position.timestamp
      };
      
      setPosition(pos);
      setGpsAvailable(true);
      
      if (onPositionUpdate) {
        onPositionUpdate(pos);
      }

      // Log detailed position data
      console.log('[GPS] Position details:', {
        coords: position.coords,
        timestamp: position.timestamp,
        date: new Date(position.timestamp).toISOString()
      });
    };

    const errorHandler = (error) => {
      clearTimeout(timeoutRef.current);
      console.error('[GPS] Geolocation error:', error);
      setGpsAvailable(false);
      
      switch(error.code) {
        case error.PERMISSION_DENIED:
          setGpsStatus(GPS_STATUS.DENIED);
          console.log('[GPS] Status: DENIED - User denied permission');
          break;
        case error.POSITION_UNAVAILABLE:
          setGpsStatus(GPS_STATUS.UNAVAILABLE);
          console.log('[GPS] Status: UNAVAILABLE - Position unavailable');
          break;
        case error.TIMEOUT:
          setGpsStatus(GPS_STATUS.TIMEOUT);
          console.log('[GPS] Status: TIMEOUT - Position request timed out');
          break;
        default:
          setGpsStatus(GPS_STATUS.UNAVAILABLE);
          console.log('[GPS] Status: UNAVAILABLE - Unknown error');
      }
    };

    // Get current position
    navigator.geolocation.getCurrentPosition(successHandler, errorHandler, GPS_OPTIONS);

    // Watch position for continuous updates
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        if (gpsStatus !== GPS_STATUS.SUCCESS) {
          setGpsStatus(GPS_STATUS.SUCCESS);
          console.log('[GPS] Status: SUCCESS (from watch)');
        }
        
        const pos = { 
          lat: position.coords.latitude, 
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          timestamp: position.timestamp
        };
        
        setPosition(pos);
        setGpsAvailable(true);
        
        if (onPositionUpdate) {
          onPositionUpdate(pos);
        }

        // Log watch updates
        console.log('[GPS] Watch update:', {
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          },
          timestamp: new Date(position.timestamp).toISOString()
        });
      },
      (error) => {
        console.error('[GPS] Watch position error:', error);
        setGpsAvailable(false);
      },
      GPS_OPTIONS
    );

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (permissionStatusRef.current && permissionStatusRef.current.onchange) {
        permissionStatusRef.current.onchange = null;
      }
    };
  }, [shouldRequest]); // Removed gpsStatus from dependencies to avoid infinite loop

  return {
    // State
    gpsStatus,
    position,
    gpsAvailable,
    orientation,
    
    // Computed properties
    isLoading: gpsStatus === GPS_STATUS.CHECKING || 
               gpsStatus === GPS_STATUS.PROMPT_PENDING || 
               gpsStatus === GPS_STATUS.WAITING,
    hasPosition: gpsStatus === GPS_STATUS.SUCCESS && position !== null,
    isError: gpsStatus === GPS_STATUS.DENIED || 
             gpsStatus === GPS_STATUS.UNAVAILABLE || 
             gpsStatus === GPS_STATUS.TIMEOUT ||
             gpsStatus === GPS_STATUS.UNSUPPORTED,
    
    // Helpers
    isSupported: navigator.geolocation !== undefined,
    isPermissionDenied: gpsStatus === GPS_STATUS.DENIED,
    isWaitingForPermission: gpsStatus === GPS_STATUS.PROMPT_PENDING,
    isWaitingForPosition: gpsStatus === GPS_STATUS.WAITING
  };
}